import { useState, useMemo } from 'react'
import { Card, Button } from 'antd'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import useGetAnalyticsData from '@forwardflow/hooks/customHooks/marketing/useGetAnalyticsData'

const OverviewCards = () => {
   const [currentSlide, setCurrentSlide] = useState(0)
   const cardsPerSlide = 4

   const { data: analyticsData } = useGetAnalyticsData()

   const convertDayNumberToName = (dayNumber: number): string => {
      const days = [
         'Sunday',
         'Monday',
         'Tuesday',
         'Wednesday',
         'Thursday',
         'Friday',
         'Saturday',
      ]
      return days[dayNumber] || 'Unknown'
   }

   const convertTo12HourFormat = (hour: number): string => {
      if (hour === 0) return '12 AM'
      if (hour < 12) return `${hour} AM`
      if (hour === 12) return '12 PM'
      return `${hour - 12} PM`
   }

   // Helper functions for agent stats
   const getBestAppointmentCloser = (agentStats: any[]) => {
      if (!agentStats || agentStats.length === 0) return null
      
      const validAgents = agentStats.filter(agent => 
         agent.conversationsPerAppointment && agent.conversationsPerAppointment > 0
      )
      
      if (validAgents.length === 0) return null
      
      return validAgents.reduce((prev, current) =>
         prev.conversationsPerAppointment < current.conversationsPerAppointment ? prev : current
      )
   }

   const getWorstConnector = (agentStats: any[]) => {
      if (!agentStats || agentStats.length === 0) return null
      
      const validAgents = agentStats.filter(agent => 
         agent.callAttemptsPerConversation && agent.callAttemptsPerConversation > 0
      )
      
      if (validAgents.length === 0) return null
      
      return validAgents.reduce((prev, current) =>
         prev.callAttemptsPerConversation > current.callAttemptsPerConversation ? prev : current
      )
   }

   const getBestLeadSourceByAppointments = (leadSources: any[]) => {
      if (!leadSources || leadSources.length === 0) return null
      
      const validSources = leadSources.filter(source => 
         source.appointments && source.appointments > 0
      )
      
      if (validSources.length === 0) return null
      
      return validSources.reduce((prev, current) =>
         prev.appointments > current.appointments ? prev : current
      )
   }

   const getTop3LeadSources = (leadSources: any[]) => {
      if (!leadSources || leadSources.length === 0) return null
      
      const validSources = leadSources.filter(source => 
         source.appointments && source.appointments > 0
      )
      
      if (validSources.length === 0) return null
      
      return validSources
         .sort((a, b) => b.appointments - a.appointments)
         .slice(0, 3)
   }

   // Get helper function results
   const bestAppointmentCloser = getBestAppointmentCloser(analyticsData?.agentStats)
   const worstConnector = getWorstConnector(analyticsData?.agentStats)
   const bestLeadSourceByAppointments = getBestLeadSourceByAppointments(analyticsData?.bestLeadSources)
   const top3LeadSources = getTop3LeadSources(analyticsData?.bestLeadSources)

   // Create cards array with conditional logic
   const allCards = useMemo(() => {
      const cards = []

      // Card 1: % of Leads Contacted in 24 Hours
      if (analyticsData?.contactedWithin24hrs && analyticsData?.totalPeople) {
         cards.push({
            id: 1,
            title: '% of Leads Contacted in 24 Hours',
            color: 'text-green-400',
            content: (
               <div className="flex items-center justify-center h-40">
                  <div className="flex items-center justify-center w-24 h-24 mx-auto mb-4">
                     <div className="relative">
                        <div className="flex items-center justify-center w-20 h-20 rounded-full shadow-lg bg-gradient-to-br from-green-500 to-green-600">
                           <div className="flex items-center justify-center w-16 h-16 bg-white rounded-full">
                              <div className="text-2xl">⏰</div>
                           </div>
                        </div>
                        <div className="absolute flex items-center justify-center w-8 h-8 text-xs font-bold text-white bg-green-600 rounded-full shadow-md -top-1 -right-1">
                           24h
                        </div>
                        <div className="absolute flex items-center justify-center w-6 h-6 bg-green-500 rounded-full shadow-md -bottom-1 -right-1">
                           <span className="text-xs text-white">📞</span>
                        </div>
                     </div>
                  </div>
               </div>
            ),
            description: (
               <div className="text-sm">
                  <p className="mb-2 text-center text-gray-300">
                     Your team is contacting
                  </p>
                  <p className="text-center text-gray-300">
                     <span className="text-2xl font-bold text-white">
                        {((analyticsData.contactedWithin24hrs / analyticsData.totalPeople) * 100).toFixed(2)}%
                     </span>{' '}
                     of all top of funnel leads within 24 hours
                  </p>
               </div>
            ),
         })
      }

      // Card 2: Best Lead Sources
      if (top3LeadSources && top3LeadSources.length > 0) {
         cards.push({
            id: 2,
            title: 'Best Lead Sources',
            color: 'text-green-400',
            content: (
               <div className="flex items-center justify-center h-40">
                  <div className="flex items-center justify-center w-24 h-24 mx-auto mb-4">
                     <div className="relative">
                        <div className="flex items-center justify-center w-12 h-12 rounded-full shadow-lg bg-gradient-to-br from-blue-500 to-blue-600">
                           <span className="text-lg text-white">🎯</span>
                        </div>
                        <div className="absolute flex items-center justify-center w-8 h-8 rounded-full shadow-md bg-gradient-to-br from-green-400 to-green-500 -top-2 -left-8">
                           <span className="text-xs text-white">👤</span>
                        </div>
                        <div className="absolute flex items-center justify-center w-8 h-8 rounded-full shadow-md bg-gradient-to-br from-purple-400 to-purple-500 -top-2 -right-8">
                           <span className="text-xs text-white">🏠</span>
                        </div>
                        <div className="absolute left-0 flex items-center justify-center w-8 h-8 rounded-full shadow-md bg-gradient-to-br from-orange-400 to-orange-500 -bottom-2">
                           <span className="text-xs text-white">🌐</span>
                        </div>
                     </div>
                  </div>
               </div>
            ),
            description: (
               <div className="text-sm">
                  <p className="text-center text-gray-300">
                     The top 3 lead sources by appointments are:{' '}
                     {top3LeadSources.map((source, index) => (
                        <span key={index} className="block font-bold text-white">
                           {source.leadSource}
                           {index < top3LeadSources.length - 1 && ','}
                        </span>
                     ))}
                  </p>
               </div>
            ),
         })
      }

      // Card 3: Appointment Set %
      if (analyticsData?.leadsWithAppointmentsSetLastDays && analyticsData?.totalLeadsLastDays) {
         cards.push({
            id: 3,
            title: 'Appointment Set %',
            color: 'text-red-400',
            content: (
               <div className="flex items-center justify-center h-40">
                  <div className="flex items-center justify-center w-24 h-24 mx-auto mb-4">
                     <div className="relative">
                        <div className="flex items-center justify-center w-20 h-20 shadow-lg bg-gradient-to-br from-red-500 to-red-600 rounded-xl">
                           <div className="flex items-center justify-center w-16 h-16 bg-white rounded-lg">
                              <div className="text-2xl">📅</div>
                           </div>
                        </div>
                        <div className="absolute flex items-center justify-center w-8 h-8 rounded-full shadow-md bg-gradient-to-br from-orange-500 to-red-500 -top-1 -right-1">
                           <span className="text-xs text-white">⚠️</span>
                        </div>
                        <div className="absolute flex items-center justify-center w-6 h-6 bg-red-600 rounded-full shadow-md -bottom-1 -left-1">
                           <span className="text-xs font-bold text-white">%</span>
                        </div>
                     </div>
                  </div>
               </div>
            ),
            description: (
               <div className="text-sm">
                  <p className="mb-2 text-center text-gray-300">
                     For all{' '}
                     <span className="font-bold text-white">
                        {analyticsData.totalLeadsLastDays}
                     </span>{' '}
                     leads created in the last 30 days, your team has set
                     appointments for
                  </p>
                  <p className="text-center text-gray-300">
                     <span className="text-2xl font-bold text-white">
                        {((analyticsData.leadsWithAppointmentsSetLastDays / analyticsData.totalLeadsLastDays) * 100).toFixed(2)}%
                     </span>{' '}
                     of them.
                  </p>
               </div>
            ),
         })
      }

      // Card 4: Leads Needing Attention
      if (analyticsData?.leadsNotContacted60_90Days) {
         cards.push({
            id: 4,
            title: 'Leads Needing Attention',
            color: 'text-gray-400',
            content: (
               <div className="flex items-center justify-center h-40">
                  <div className="flex items-center justify-center w-24 h-24 mx-auto mb-4">
                     <div className="relative">
                        <div className="flex items-center justify-center w-20 h-20 rounded-t-full rounded-b-lg shadow-lg bg-gradient-to-br from-amber-600 to-amber-700">
                           <div className="flex items-center justify-center w-16 h-16 rounded-t-full rounded-b-lg bg-gradient-to-br from-amber-100 to-amber-200">
                              <div className="text-2xl">🌱</div>
                           </div>
                        </div>
                        <div className="absolute flex items-center justify-center w-8 h-8 rounded-full shadow-md bg-gradient-to-br from-yellow-500 to-orange-500 animate-pulse -top-1 -right-1">
                           <span className="text-xs text-white">!</span>
                        </div>
                     </div>
                  </div>
               </div>
            ),
            description: (
               <div className="text-sm">
                  <p className="mb-2 text-center text-gray-300">
                     Your active and lead nurture stages are showing
                  </p>
                  <p className="text-center text-gray-300">
                     <span className="text-2xl font-bold text-white">
                        {analyticsData.leadsNotContacted60_90Days}
                     </span>{' '}
                     leads not contacted in{' '}
                     <span className="font-bold text-white">60-90</span> days.
                  </p>
               </div>
            ),
         })
      }

      // Card 5: Best Time to Call
      if (analyticsData?.bestTimeToCall?.callDay !== undefined && analyticsData?.bestTimeToCall?.callHour !== undefined) {
         cards.push({
            id: 5,
            title: 'Best Time to Call',
            color: 'text-green-400',
            content: (
               <div className="flex items-center justify-center h-40">
                  <div className="flex items-center justify-center w-24 h-24 mx-auto mb-4">
                     <div className="relative">
                        <div className="flex items-center justify-center w-20 h-20 shadow-lg bg-gradient-to-br from-green-500 to-green-600 rounded-2xl">
                           <div className="flex items-center justify-center w-16 h-16 bg-white rounded-xl">
                              <div className="text-2xl">📱</div>
                           </div>
                        </div>
                        <div className="absolute flex items-center justify-center w-10 h-6 transform -translate-x-1/2 rounded-full shadow-md bg-gradient-to-r from-blue-500 to-blue-600 -top-2 left-1/2">
                           <span className="text-xs font-bold text-white">
                              {convertTo12HourFormat(analyticsData.bestTimeToCall.callHour).slice(0, -3)}
                           </span>
                        </div>
                     </div>
                  </div>
               </div>
            ),
            description: (
               <div className="text-sm">
                  <p className="text-center text-gray-300">
                     The best time to call your leads{' '}
                     <span className="text-lg font-bold text-white">
                        {convertDayNumberToName(analyticsData.bestTimeToCall.callDay)}{' '}
                        at{' '}
                        {convertTo12HourFormat(analyticsData.bestTimeToCall.callHour)}
                     </span>
                     .
                  </p>
               </div>
            ),
         })
      }

      // Card 6: Top Appointment Closer
      if (bestAppointmentCloser) {
         cards.push({
            id: 6,
            title: 'Top Appointment Closer',
            color: 'text-green-400',
            content: (
               <div className="flex items-center justify-center h-40">
                  <div className="flex items-center justify-center w-24 h-24 mx-auto mb-4">
                     <div className="relative">
                        <div className="flex items-center justify-center w-20 h-20 rounded-full shadow-lg bg-gradient-to-br from-yellow-500 to-yellow-600">
                           <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-yellow-200 to-yellow-300">
                              <div className="text-2xl">🏆</div>
                           </div>
                        </div>
                        <div className="absolute flex items-center justify-center w-8 h-8 rounded-full shadow-md bg-gradient-to-br from-green-500 to-green-600 -top-1 -right-1">
                           <span className="text-xs font-bold text-white">#1</span>
                        </div>
                     </div>
                  </div>
               </div>
            ),
            description: (
               <div className="text-sm">
                  <p className="text-center text-gray-300">
                     The agent that's created the most appointments with the fewest
                     number of conversations{' '}
                     <span className="text-lg font-bold text-white">
                        {bestAppointmentCloser.agentName}
                     </span>
                     .
                  </p>
               </div>
            ),
         })
      }

      // Card 7: Struggling to Connect
      if (worstConnector) {
         cards.push({
            id: 7,
            title: 'Struggling to Connect',
            color: 'text-red-400',
            content: (
               <div className="flex items-center justify-center h-40">
                  <div className="flex items-center justify-center w-24 h-24 mx-auto mb-4">
                     <div className="relative">
                        <div className="flex items-center justify-center w-20 h-20 shadow-lg bg-gradient-to-br from-red-500 to-red-600 rounded-2xl">
                           <div className="flex items-center justify-center w-16 h-16 bg-white rounded-xl">
                              <div className="text-2xl">📞</div>
                           </div>
                        </div>
                        <div className="absolute flex items-center justify-center w-8 h-8 rounded-full shadow-md bg-gradient-to-br from-orange-500 to-red-500 -top-1 -right-1">
                           <span className="text-xs font-bold text-white">⚠️</span>
                        </div>
                     </div>
                  </div>
               </div>
            ),
            description: (
               <div className="text-sm">
                  <p className="text-center text-gray-300">
                     The agent that's struggling the most to turn outbound calls
                     into conversations{' '}
                     <span className="text-lg font-bold text-white">
                        {worstConnector.agentName}
                     </span>
                     .
                  </p>
               </div>
            ),
         })
      }

      // Card 8: Source with Most Appointments
      if (bestLeadSourceByAppointments) {
         cards.push({
            id: 8,
            title: 'Source with Most Appointments',
            color: 'text-green-400',
            content: (
               <div className="flex items-center justify-center h-40">
                  <div className="flex items-center justify-center w-24 h-24 mx-auto mb-4">
                     <div className="relative">
                        <div className="flex items-center justify-center w-20 h-20 rounded-full shadow-lg bg-gradient-to-br from-purple-500 to-purple-600">
                           <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-200 to-purple-300">
                              <div className="text-2xl">⭐</div>
                           </div>
                        </div>
                        <div className="absolute flex items-center justify-center w-8 h-8 transform -translate-x-1/2 rounded-full shadow-md bg-gradient-to-br from-yellow-500 to-yellow-600 -top-1 left-1/2">
                           <span className="text-xs">👑</span>
                        </div>
                     </div>
                  </div>
               </div>
            ),
            description: (
               <div className="text-sm">
                  <p className="text-center text-gray-300">
                     The lead source that's given your team the most appointments
                     in the last 30 days{' '}
                     <span className="text-lg font-bold text-white">
                        {bestLeadSourceByAppointments.leadSource}
                     </span>
                     .
                  </p>
               </div>
            ),
         })
      }

      return cards
   }, [analyticsData, bestAppointmentCloser, worstConnector, bestLeadSourceByAppointments, top3LeadSources])

   const totalSlides = Math.ceil(allCards.length / cardsPerSlide)

   const nextSlide = () => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides)
   }

   const prevSlide = () => {
      setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides)
   }

   // Get current cards to display
   const startIndex = currentSlide * cardsPerSlide
   const currentCards = allCards.slice(startIndex, startIndex + cardsPerSlide)

   // If no cards available, show a message
   if (allCards.length === 0) {
      return (
         <div className="w-full mx-auto">
            <div className="px-6 py-4 mb-4 text-white rounded-lg bg-primary-dark">
               <h2 className="text-lg font-medium">
                  OVERVIEW & GROWTH OPPORTUNITIES
               </h2>
            </div>
            <div className="py-8 text-center rounded-xl backdrop-blur-sm">
               <p className="text-gray-400">No data available to display overview cards.</p>
            </div>
         </div>
      )
   }

   return (
      <div className="w-full mx-auto">
         {/* Header */}
         <div className="px-6 py-4 mb-4 text-white rounded-lg bg-primary-dark">
            <h2 className="text-lg font-medium">
               OVERVIEW & GROWTH OPPORTUNITIES
            </h2>
         </div>

         {/* Cards Container */}
         <div className="py-8 rounded-xl backdrop-blur-sm">
            <div className="relative">
               {/* Navigation Arrows */}
               {totalSlides > 1 && (
                  <>
                     <Button
                        icon={<LeftOutlined />}
                        onClick={prevSlide}
                        className="absolute left-0 z-10 flex items-center justify-center !w-12 h-12 text-white transform -translate-x-12 -translate-y-1/2 bg-primary rounded-full shadow-md shrink-0 top-1/2 hover:bg-primary-dark transition-colors duration-200"
                     />
                     <Button
                        icon={<RightOutlined />}
                        onClick={nextSlide}
                        className="absolute right-0 z-10 flex items-center justify-center !w-12 h-12 text-white transform translate-x-12 -translate-y-1/2 rounded-full shadow-md shrink-0 bg-primary top-1/2 hover:bg-primary-dark transition-colors duration-200"
                     />
                  </>
               )}

               {/* Cards Grid */}
               <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                  {currentCards.map((card) => (
                     <Card
                        key={card.id}
                        className="relative shadow-2xl bg-gradient-to-br from-[#2a2438] to-[#232033] border-[#3a3548] border-2 h-full transition-all duration-200 group hover:shadow-xl"
                     >
                        <div className="mb-4">
                           <h3
                              className={`text-sm font-semibold text-center ${card.color}`}
                           >
                              {card.title}
                           </h3>
                        </div>
                        {card.content}
                        {card.description}
                     </Card>
                  ))}
               </div>
            </div>

            {/* Pagination Dots */}
            {totalSlides > 1 && (
               <div className="flex justify-center mt-8 space-x-3">
                  {Array.from({ length: totalSlides }).map((_, index) => (
                     <div
                        key={index}
                        className={`w-3 h-3 rounded-full cursor-pointer transition-all duration-200 ${
                           index === currentSlide
                              ? 'bg-primary scale-110 shadow-lg'
                              : 'bg-gray-500 hover:bg-gray-400'
                        }`}
                        onClick={() => setCurrentSlide(index)}
                     ></div>
                  ))}
               </div>
            )}
         </div>
      </div>
   )
}

export default OverviewCards